import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useCurrentUser } from '../../context/CurrentUserContext';
import { canAccessRoute, getDefaultRouteForRole, isGrowthOnly } from '../../utils/routeAccess';

interface PortalAccessGateProps {
  children: React.ReactNode;
}

/**
 * Wraps the whole signed-in portal (the Layout and every page below it). Nothing below it mounts,
 * and so no page query fires, until the user's roles are known (YOJO-3614 [growth staff role +
 * growth MCP door]):
 *  - while the roles query loads: a spinner, for every role;
 *  - if the roles query failed: "We couldn't load your access" with a Retry. It fails closed for
 *    every role: no portal and no redirect, since without roles there is nowhere safe to send them;
 *  - a growth-only user, on any page outside their own profile: sent to it, with no toast. Growth
 *    works through MCP and must reach no member or customer page, so this is an allow-list. It also
 *    covers /test-auth's /dashboard, stale links and pages with no role guard of their own.
 * Every other role, once its roles are known, gets `children` untouched.
 */
const PortalAccessGate: React.FC<PortalAccessGateProps> = ({ children }) => {
  const { roles, loading, error, refetchRoles } = useCurrentUser();
  const { pathname } = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="roles-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="roles-load-error">
        <div role="alert" className="flex items-center gap-x-3 text-sm text-gray-700">
          <span>We couldn&apos;t load your access.</span>
          <button
            type="button"
            onClick={refetchRoles}
            data-testid="roles-load-error-retry"
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (isGrowthOnly(roles) && !canAccessRoute(pathname, roles)) {
    return <Navigate to={getDefaultRouteForRole(roles)} replace />;
  }

  return <>{children}</>;
};

export default PortalAccessGate;
